// packages/client/src/components/project/miller/SheetColumn.tsx
import { useRef, useMemo, useState } from 'react';
import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '@heroui/react';
import { Plus, FileText, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useRevisions, useUploadRevision, useDeleteRevision } from '@/api/drawing-sets';
import StatusBadge from './StatusBadge';
import type { DrawingSet, DrawingRevision } from '@estimator/shared';

// A "sheet" is a group of revisions sharing the same knowledge_item lineage.
// Since each revision upload creates a new knowledge_item, we treat each revision
// as a sheet entry and show the latest revision's status.
// The RevisionColumn will then show the full revision history for that sheet.
interface Sheet {
  knowledgeItemId: string;
  revisionId: string;
  title: string;
  latestRevision: DrawingRevision;
  revisionCount: number;
}

interface SheetColumnProps {
  projectId: string;
  drawingSet: DrawingSet;
  selectedSheetKnowledgeItemId: string | null;
  onSelectSheet: (sheet: Sheet) => void;
}

export default function SheetColumn({
  projectId,
  drawingSet,
  selectedSheetKnowledgeItemId,
  onSelectSheet,
}: SheetColumnProps) {
  const { data: revisionsResponse } = useRevisions(drawingSet.id);
  const revisions = revisionsResponse?.data ?? [];
  const uploadRevision = useUploadRevision(drawingSet.id);
  const deleteRevision = useDeleteRevision(drawingSet.id);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);

  // Each revision is effectively a "sheet" — the latest uploaded PDF in the set.
  // Group by revision lineage: each upload is a new sheet or a new revision of an existing sheet.
  // For now, each revision = one sheet entry (per spec: each sheet is a distinct knowledge_item).
  const sheets: Sheet[] = useMemo(() => {
    return revisions.map((rev) => ({
      knowledgeItemId: rev.knowledge_item_id,
      revisionId: rev.id,
      title: rev.title ?? `Sheet ${rev.revision_number}`,
      latestRevision: rev,
      revisionCount: 1,
    }));
  }, [revisions]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadRevision.mutateAsync({ file, projectId });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    const { title } = deleteTarget;
    deleteRevision.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success(`${title} deleted`);
        setDeleteTarget(null);
      },
      onError: (err) => {
        toast.error(`Failed to delete: ${err.message}`);
        setDeleteTarget(null);
      },
    });
  };

  return (
    <div
      className="flex flex-col h-full border-r"
      style={{ borderColor: 'var(--border)', minWidth: 220 }}
    >
      <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
        <h3 className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
          Sheets
        </h3>
        <Button
          size="sm"
          variant="light"
          isIconOnly
          onPress={() => fileInputRef.current?.click()}
          isLoading={uploadRevision.isPending}
          title="Upload Sheet"
        >
          <Plus size={14} />
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {sheets.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No sheets yet</p>
          </div>
        ) : (
          sheets.map((sheet) => (
            <div
              key={sheet.knowledgeItemId}
              className="group flex items-center gap-2 px-3 py-2 text-sm transition-colors cursor-pointer"
              style={{
                backgroundColor: selectedSheetKnowledgeItemId === sheet.knowledgeItemId ? 'var(--bg-hover)' : 'transparent',
                color: selectedSheetKnowledgeItemId === sheet.knowledgeItemId ? 'var(--accent)' : 'var(--text-primary)',
              }}
              onClick={() => onSelectSheet(sheet)}
            >
              <FileText size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
              <span className="flex-1 truncate">{sheet.title}</span>
              <StatusBadge status={sheet.latestRevision.status} compact />
              <button
                className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0 p-0.5 rounded hover:bg-[var(--bg-hover)]"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget({ id: sheet.revisionId, title: sheet.title });
                }}
                title="Delete sheet"
              >
                <Trash2 size={12} style={{ color: 'var(--text-muted)' }} />
              </button>
            </div>
          ))
        )}
      </div>

      {/* Delete confirmation modal */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} size="sm">
        <ModalContent style={{ backgroundColor: 'var(--bg-surface)' }}>
          <ModalHeader className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Delete Sheet
          </ModalHeader>
          <ModalBody>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{deleteTarget?.title}</strong>? This cannot be undone.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              size="sm"
              variant="bordered"
              onPress={() => setDeleteTarget(null)}
              className="border-[var(--border)] text-[var(--text-secondary)]"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              color="danger"
              onPress={handleDelete}
              isLoading={deleteRevision.isPending}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
